import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import {
  canAssignOtherAgents,
  filterAssignableAgents,
  getAgentsByUpdatedPresence,
  getSortedAgentsByAvailability,
} from 'dashboard/helper/agentHelper';

/**
 * A composable function that provides a list of agents for assignment.
 *
 * @param {boolean} [includeNoneAgent=true] - Whether to include a 'None' agent option.
 * @param {Object} [options]
 * @param {boolean} [options.forAssignment=false] - The list is used to assign the conversation:
 *   users who are not administrators (of the account or of the conversation's team) only get
 *   themselves, because agents work by team and hand conversations to a team, not to a colleague.
 * @returns {Object} An object containing the agents list and assignable agents.
 */
export function useAgentsList(includeNoneAgent = true, options = {}) {
  const { forAssignment = false } = options;
  const { t } = useI18n();
  const currentUser = useMapGetter('getCurrentUser');
  const currentChat = useMapGetter('getSelectedChat');
  const currentAccountId = useMapGetter('getCurrentAccountId');
  const assignable = useMapGetter('inboxAssignableAgents/getAssignableAgents');
  const currentRole = forAssignment ? useMapGetter('getCurrentRole') : null;

  const inboxId = computed(() => currentChat.value?.inbox_id);
  const isAgentSelected = computed(() => currentChat.value?.meta?.assignee);

  /**
   * Creates a 'None' agent object
   * @returns {Object} None agent object
   */
  const createNoneAgent = () => ({
    confirmed: true,
    name: t('AGENT_MGMT.MULTI_SELECTOR.LIST.NONE') || 'None',
    id: 0,
    role: 'agent',
    account_id: 0,
    email: 'None',
  });

  /**
   * @type {import('vue').ComputedRef<Array>}
   */
  const assignableAgents = computed(() => {
    return inboxId.value ? assignable.value(inboxId.value) : [];
  });

  /**
   * @type {import('vue').ComputedRef<Array>}
   */
  const canAssignOthers = computed(
    () =>
      !forAssignment ||
      canAssignOtherAgents({
        role: currentRole.value,
        administeredTeamIds: currentUser.value?.administered_team_ids,
        teamId: currentChat.value?.meta?.team?.id,
      })
  );

  const agentsList = computed(() => {
    const agents = filterAssignableAgents(
      assignableAgents.value || [],
      canAssignOthers.value,
      currentUser.value?.id
    );
    const agentsByUpdatedPresence = getAgentsByUpdatedPresence(
      agents,
      currentUser.value,
      currentAccountId.value
    );

    const filteredAgentsByAvailability = getSortedAgentsByAvailability(
      agentsByUpdatedPresence
    );

    return [
      ...(includeNoneAgent && isAgentSelected.value ? [createNoneAgent()] : []),
      ...filteredAgentsByAvailability,
    ];
  });

  return {
    agentsList,
    assignableAgents,
  };
}
